import React from 'react'
import { observer } from 'mobx-react-lite'

import { useTheme } from '@mui/material'
import List from '@mui/material/List'
import Stack from '@mui/material/Stack'
import { Text } from '@mxenabled/mxui'

import { ANALYTICS_EVENTS, useEvent, useGlobalCopyStore } from '../../../common'
import { DATE_FORMATS_INTL, formatDate } from '../../../core/constants'
import { useDebtsUiStore } from '../../hooks/useDebtsUiStore'
import { formatCurrency } from '../../../core/utils/NumberFormatting'

const DebtScheduleTab = () => {
  const theme = useTheme()
  const { onEvent } = useEvent()
  const { debts: copy } = useGlobalCopyStore()
  const { selectedDebtChartData: debt, showCompleted, showError } = useDebtsUiStore()

  React.useEffect(() => onEvent(ANALYTICS_EVENTS.DEBT_SCHEDULE_VIEW), [])

  return (
    <List>
      {/* Error/Complete Message */}
      {showError || showCompleted ? (
        <Text
          color={theme.palette.secondary.main}
          component="div"
          sx={{ p: 32, textAlign: 'center' }}
          variant="body2"
        >
          {showError ? copy.schedule_error_message : copy.schedule_complete_message}
        </Text>
      ) : (
        <>
          {/* Payment Schedule Header */}
          <Stack flexDirection="row" justifyContent="space-between" sx={{ pt: 12, px: 24, pb: 8 }}>
            <Text bold={true} variant="caption">
              {copy.schedule_month_heading}
            </Text>
            <Text bold={true} variant="caption">
              {copy.schedule_amount_heading}
            </Text>
          </Stack>
          {/* Payment Schedule Rows */}
          {debt.dataset.slice(1).map(({ x: month, y: balance, payment }) => (
            <Stack bgcolor={theme.palette.background.paper} key={String(month)}>
              <Stack alignItems="center" flexDirection="row" pl={24}>
                <Stack
                  sx={{
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    flexDirection: 'row',
                    flexGrow: 1,
                    py: 14,
                    pr: 24,
                  }}
                >
                  <Stack flexGrow={1}>
                    <Text bold={true} variant="body1">
                      {formatDate(month, DATE_FORMATS_INTL.MONTH_YEAR)}
                    </Text>
                    <Text color={theme.palette.secondary.main} variant="caption">
                      {copy.schedule_month_label}
                    </Text>
                  </Stack>
                  <Stack>
                    <Text bold={true} variant="body1">
                      {formatCurrency(balance, '0,0.00')}
                    </Text>
                    <Text
                      bold={true}
                      color={theme.palette.success.main}
                      textAlign="end"
                      variant="tiny"
                    >
                      {`+${formatCurrency(payment, '0,0')}`}
                    </Text>
                  </Stack>
                </Stack>
              </Stack>
            </Stack>
          ))}
          {/* Debt Payoff Row */}
          <Stack bgcolor="background.paper">
            <Stack
              sx={{
                alignItems: 'center',
                flexDirection: 'row',
                flexGrow: 1,
                py: 14,
                px: 24,
              }}
            >
              <Text flexGrow={1} variant="body1">
                {copy.schedule_payoff_label}
              </Text>
              <Text variant="body1">-</Text>
            </Stack>
          </Stack>
        </>
      )}
    </List>
  )
}

export default observer(DebtScheduleTab)
